import { applyFormatters, formatCurrencyValue, formatMeasurementValue } from './formatters';
import { formatDisplayTime, formatShortDateByPrecision } from './dateDisplay';
import { earliestDateValue, formatDateValue, MULTI_DATE_TYPES, normalizeDateList, precisionOf } from './dateOptions';
import { markdownToPlainText } from './markdown';
import { formatBooleanValue, numberFormatOptions } from './elements';
import { formatNumber } from './elementFormat/number';
import { formatEmail } from './elementFormat/email';
import { formatLocation } from './elementFormat/location';
import { formatPhone } from './elementFormat/phone';
import { choiceLabelOf } from './choiceValues';
import { isCode } from './recordCodes';
import { isMaskedElement } from './maskedElements';
import { siteOrigin } from '../platform';

const TOKEN_REGEX = /{{\s*([^}]+)\s*}}/g;

const parseFormatterExpression = (expr = '') => {
	const trimmed = expr.trim();
	if (!trimmed) return null;
	const match = trimmed.match(/^([a-zA-Z0-9_-]+)(?:\((.*)\))?$/);
	if (!match) return null;
	const name = match[1].trim();
	const argsRaw = match[2];
	if (!name) return null;
	const args = [];
	if (typeof argsRaw === 'string') {
		let buffer = '';
		let inQuotes = false;
		let quoteChar = '';
		// A quoted buffer is pushed even when empty so '' can hold an arg's
		// position (args are positional; dropping one would shift the rest),
		// and quoted content keeps its whitespace verbatim.
		let bufferQuoted = false;
		const flush = () => {
			const out = bufferQuoted ? buffer : buffer.trim();
			if (out || bufferQuoted) args.push(out);
			buffer = '';
			bufferQuoted = false;
		};
		for (let i = 0; i < argsRaw.length; i += 1) {
			const char = argsRaw[i];
			if (inQuotes) {
				if (char === quoteChar) {
					inQuotes = false;
					continue;
				}
				buffer += char;
				continue;
			}
			if (char === '"' || char === '\'') {
				inQuotes = true;
				quoteChar = char;
				bufferQuoted = true;
				if (!buffer.trim()) buffer = '';
				continue;
			}
			if (char === ',') {
				flush();
				continue;
			}
			buffer += char;
		}
		flush();
	}
	return { name, args };
};

const toStringValue = (val) => {
	if (val === undefined || val === null) return '';
	return String(val);
};

export function parseDisplayTemplate(template = '') {
	if (!template) return [];
	const segments = [];
	let lastIndex = 0;
	template.replace(TOKEN_REGEX, (match, rawToken, offset) => {
		if (offset > lastIndex) {
			segments.push({ type: 'text', value: template.slice(lastIndex, offset) });
		}
		const tokenParts = (rawToken || '').split('|').map(part => part.trim()).filter(Boolean);
		const name = tokenParts.shift();
		if (name) {
			const formatters = tokenParts
				.map(part => parseFormatterExpression(part))
				.filter(Boolean);
			segments.push({ type: 'element', name, formatters });
		} else {
			segments.push({ type: 'text', value: match });
		}
		lastIndex = offset + match.length;
		return match;
	});
	if (lastIndex < template.length) {
		segments.push({ type: 'text', value: template.slice(lastIndex) });
	}
	return segments;
}

//////////////////////////////////////////////////
//
//   STORED ENCODING (v2 JSON envelope)
//
//////////////////////////////////////////////////
//
// Templates persist in the card's `displayTemplate` string field. New saves
// write a versioned JSON envelope:
//
//   {"v":2,"segments":[{"type":"text","value":"Rx: "},
//                      {"type":"element","name":"person",
//                       "formatters":[{"name":"initials","args":[]}]}]}
//
// Text is data, not syntax, so no user input can break parsing. Legacy
// templates in the {{name | fn(args)}} DSL are still read via
// parseDisplayTemplate — parseStoredTemplate sniffs the format ("{{..." fails
// JSON.parse and falls through). Cards upgrade to v2 the next time they save.

export const STORED_TEMPLATE_VERSION = 2;

const sanitizeSegments = (segments) => {
	if (!Array.isArray(segments)) return [];
	const out = [];
	segments.forEach(segment => {
		if (!segment || typeof segment !== 'object') return;
		if (segment.type === 'text') {
			const value = typeof segment.value === 'string' ? segment.value : toStringValue(segment.value);
			if (value) out.push({ type: 'text', value });
			return;
		}
		if (segment.type === 'element' && typeof segment.name === 'string' && segment.name) {
			const formatters = (Array.isArray(segment.formatters) ? segment.formatters : [])
				.filter(fmt => fmt && typeof fmt.name === 'string' && fmt.name)
				.map(fmt => ({
					name: fmt.name,
					args: (Array.isArray(fmt.args) ? fmt.args : []).map(arg => (arg === undefined || arg === null ? '' : arg)),
				}));
			out.push({ type: 'element', name: segment.name, formatters });
		}
	});
	return out;
};

// Read a stored template in either encoding into segments.
export function parseStoredTemplate(raw) {
	if (!raw) return [];
	const str = String(raw).trim();
	// v2 JSON envelope starts with '{"'; a legacy template starting with a
	// token starts with '{{' and fails JSON.parse, falling through safely.
	if (str.startsWith('{')) {
		try {
			const parsed = JSON.parse(str);
			if (parsed && parsed.v === STORED_TEMPLATE_VERSION && Array.isArray(parsed.segments)) {
				return sanitizeSegments(parsed.segments);
			}
		} catch (_) {
			// not JSON — treat as legacy DSL
		}
	}
	return parseDisplayTemplate(String(raw));
}

// Encode segments for storage. Empty content encodes as '' so existing
// "no template set" falsy checks keep working.
export function encodeStoredTemplate(segments) {
	const cleaned = sanitizeSegments(segments);
	if (!cleaned.length) return '';
	return JSON.stringify({ v: STORED_TEMPLATE_VERSION, segments: cleaned });
}

// Human-readable one-liner for summaries: 'Rx: {Person} - {Dose}'.
export function describeStoredTemplate(raw, elementLabelMap = null) {
	const segments = parseStoredTemplate(raw);
	if (!segments.length) return '';
	return segments.map(segment => {
		if (segment.type === 'text') return segment.value;
		const label = elementLabelMap?.get?.(segment.name) || segment.name;
		return `{${label}}`;
	}).join('');
}

export function renderDisplayTemplate(template = '', resolver = () => '') {
	if (!template) return '';
	const segments = parseStoredTemplate(template);
	return segments.map(segment => {
		if (segment.type === 'element') {
			return toStringValue(resolver(segment) ?? '');
		}
		return segment.value;
	}).join('');
}

// Quote an argument so it survives re-parsing: plain words/numbers pass
// through bare; anything with commas, parens, spaces, or pipes is quoted
// (double quotes when the value itself contains a single quote — the parser
// supports both quote styles but not escapes, so the other style is dropped).
const encodeFormatterArg = (arg) => {
	const str = String(arg ?? '');
	if (/^[a-zA-Z0-9_.:/-]*$/.test(str)) return str;
	if (str.includes('\'')) {
		return `"${str.replace(/"/g, '')}"`;
	}
	return `'${str}'`;
};

export function stringifyDisplayTemplate(segments = []) {
	if (!Array.isArray(segments) || !segments.length) return '';
	return segments.map(segment => {
		if (segment.type === 'text') {
			return segment.value || '';
		}
		if (segment.type === 'element') {
			const formatterParts = (segment.formatters || [])
				.map(fmt => {
					if (!fmt?.name) return null;
					// Drop trailing empty args; keep interior empties as '' so
					// positions survive the round trip.
					const trimmed = [...(fmt.args || [])];
					while (trimmed.length && (trimmed[trimmed.length - 1] === undefined || trimmed[trimmed.length - 1] === null || trimmed[trimmed.length - 1] === '')) {
						trimmed.pop();
					}
					const args = trimmed.length
						? `(${trimmed.map(a => (a === undefined || a === null || a === '' ? '\'\'' : encodeFormatterArg(a))).join(',')})`
						: '';
					return `| ${fmt.name}${args}`;
				})
				.filter(Boolean)
				.join(' ');
			return `{{${segment.name}${formatterParts ? ' ' + formatterParts : ''}}}`;
		}
		return '';
	}).join('');
}

const firstNonEmptyString = (candidates = []) => {
	for (const value of candidates) {
		if (typeof value === 'string' && value.trim().length) {
			return value.trim();
		}
	}
	return '';
};

/**
 * Default rendering for a value promoted to a title by the FALLBACK chain
 * (no display template, or it rendered empty): a raw stored date is an ISO
 * string, and a raw rich-text value is markdown — neither is a title. The
 * template path stays untouched: there the author chose the formatters, and
 * a bare token renders whatever is stored.
 */
/**
 * What a template token's formatter chain starts from. A measurement reaches
 * text formatters (upper, truncate, ...) already formatted — `upper` on the
 * stored object would print "[OBJECT OBJECT]" — and only a leading
 * `unit(...)` sees the stored object. Every other type passes through as
 * stored. Mirrors keepr-api utils/displayTitle.js so titles agree on both
 * sides; the editor preview renders through this same rule.
 */
const NUMBER_DATA_TYPES = new Set(['number', 'decimal', 'integer']);

export const templateTokenInput = (def, raw, formatters) => {
	// A file's value is an attachment id: a title never prints it (PR 10b;
	// the editor offers no file token, and a template that names one from
	// before reads as nothing). Nor does a "Me too" count (KPR-390): it
	// changes without the item being saved, so the title the API stored would
	// be wrong after the next confirmation (keepr-api utils/displayTitle.js).
	if (def?.dataType === 'file' || def?.dataType === 'confirmations') return '';
	// Nor a MASKED value (KPR-1348): a title is printed everywhere the item
	// is. keepr-api refuses a template that names one; this is the second lock.
	if (isMaskedElement(def)) return '';
	if (def?.dataType === 'measurement') {
		return formatters?.[0]?.name !== 'unit' ? formatMeasurementValue(raw, def.options) : raw;
	}
	// Money enters the chain formatted ("$1,234.50"): no formatter reads the
	// stored { amount, currency } object, and `upper` on it would print
	// "[OBJECT OBJECT]". Mirrors keepr-api utils/displayTitle.js.
	if (def?.dataType === 'currency') return formatCurrencyValue(raw, def.options);
	// The two types whose STORED shape is not the shape a person reads enter
	// the formatter chain already rendered: a location as its address (or its
	// coordinates), a phone in the spelling its country dials. Every text
	// formatter then means something here — `substring` cuts what the reader
	// sees rather than an E.164 prefix — and `upper` can never return
	// '[OBJECT OBJECT]'.
	if (def?.dataType === 'location') return formatLocation(raw);
	if (def?.dataType === 'phone') return formatPhone(raw, def?.options?.country);
	// A LIST of dates enters the chain canonical — ascending, de-duplicated —
	// so a title reads the same order the tile, the read view and the sort do,
	// whatever order the row happens to hold them in. The formatters then run
	// per entry (utils/formatters applyFormatters).
	if (MULTI_DATE_TYPES.includes(def?.dataType) && Array.isArray(raw)) {
		return normalizeDateList(def.dataType, raw);
	}
	// A yes/no prints as its element's own two words, here as everywhere.
	if (def?.dataType === 'boolean') return formatBooleanValue(def, raw) || raw;
	// A choice enters the chain as its LABEL — the owner's ruling B15: a title
	// reads "Welding, Rigging", not "welding, rigging", for one choice or
	// several. An entry the list no longer names reads as stored (keepr-api
	// utils/displayTitle.js choiceLabel, the same rule).
	if (def?.dataType === 'choice') return choiceTitleInput(def, raw);
	// A number's decimals and leading zeros are part of how it READS, so a
	// bare token honours them. A formatter chain gets the stored number
	// instead: round / thousands / currency all Number() their input, and a
	// padded string would be a display of a display.
	//
	// Only a real NUMBER is padded, which is the API's rule to the letter
	// (utils/displayTitle.js applyFormatters: `typeof output === 'number'`).
	// A legacy numeric STRING under a number element is shown as it is
	// stored on both sides — the alternative is a title that reads '0007'
	// here and '007' in the displayValue the server saved.
	if (NUMBER_DATA_TYPES.has(def?.dataType) && typeof raw === 'number' && !formatters?.length) {
		return formatNumber(raw, numberFormatOptions(def.dataType, def.options)) || raw;
	}
	return raw;
};

/** A choice value (or a list of them) as its label(s); blank stays blank. */
const choiceTitleInput = (def, raw) => {
	const one = v => ((typeof v === 'string' || typeof v === 'number') && String(v) !== '' ? choiceLabelOf(def, v) : v);
	return Array.isArray(raw) ? raw.map(one) : one(raw);
};

const formatFallbackValue = (def, value) => {
	// A file's id is a string the guard below would promote to a title: an
	// item is never called by its attachment id (PR 10b). A "Me too" count
	// is a number the API's displayTitle never promotes either (KPR-390).
	if (def?.dataType === 'file' || def?.dataType === 'confirmations') return '';
	// A masked value never names an item (KPR-1348; keepr-api displayTitle).
	if (isMaskedElement(def)) return '';
	// A measurement is a stored object (or a legacy bare number): the shared
	// formatter is the only rendering that is a title.
	if (def?.dataType === 'measurement') return formatMeasurementValue(value, def?.options) || '';
	if (def?.dataType === 'currency') return formatCurrencyValue(value, def?.options) || '';
	// A number and a yes/no are titles too — the API's displayTitle promotes
	// both, so a rendered title here must not drift from the stored
	// displayValue. Each comes through the same formatter the rest of the
	// platform prints it with (007, 12.50, "Paid").
	if (def?.dataType === 'boolean') return formatBooleanValue(def, value);
	// A location is stored as an OBJECT, so the string guard below would drop
	// it — and an address is very often exactly what the item should be
	// called.
	if (def?.dataType === 'location') return formatLocation(value);
	// A date element may hold SEVERAL dates, and a list is a stored ARRAY that
	// the string guard below would drop. Each entry reads at the element's own
	// precision, joined ascending (utils/dateOptions).
	if (MULTI_DATE_TYPES.includes(def?.dataType)) return formatDateValue(def, value) || '';
	// A Choice — one value or several — promotes its LABELS, joined with ", "
	// (B15; keepr-api utils/displayTitle.js formatFallbackValue).
	if (def?.dataType === 'choice' && (Array.isArray(value) || typeof value === 'string' || typeof value === 'number')) {
		const entries = Array.isArray(value) ? value : [value];
		return entries
			.map(entry => ((typeof entry === 'string' || typeof entry === 'number') && String(entry).trim() ? String(choiceLabelOf(def, entry)).trim() : ''))
			.filter(Boolean)
			.join(', ');
	}
	// A real number only, the same guard the API's formatFallbackValue uses
	// (`typeof value === 'number'`): a legacy numeric STRING falls through to
	// the string case below and is shown as it is stored, on both sides.
	if (NUMBER_DATA_TYPES.has(def?.dataType) && typeof value === 'number') {
		const formatted = formatNumber(value, numberFormatOptions(def.dataType, def.options));
		if (formatted) return formatted;
	}
	if (typeof value !== 'string' || !value.trim().length) return '';
	switch (def?.dataType) {
		// E.164 is the storage spelling; a title reads the dialled one.
		case 'phone':
			return formatPhone(value, def?.options?.country);
		case 'email':
			return formatEmail(value);
		// ('date' and 'date-time' are handled above, where an array can still
		// be read; a scalar takes the identical path through formatDateValue.)
		// 'HH:mm' is not a shape dayjs reads; without this a title showed the
		// stored string (or, from the old input, a whole ISO timestamp).
		case 'time':
			return formatDisplayTime(value) || '';
		case 'rich-text':
			return markdownToPlainText(value).trim();
		default:
			return value.trim();
	}
};

const DATE_TYPES = new Set(['date', 'date-time']);

/**
 * The default title of a card with no display template. The first
 * non-empty NON-date field in definition order names the item (the person
 * of a reading, the subject of a note); if the card also stamps a date, a
 * short date follows it — "Molly · Sep 9" — so time-stamped items read as
 * "who, when" without repeating the full ISO date the Updated column already
 * shows. A card whose only value is a date falls back to the full date.
 */
const composeDefaultDisplayValue = (fallbackFields, elementDefinitionMap, normalizedElements) => {
	let identity = '';
	let dateRaw = null;
	let dateDef = null;
	let dateFallback = '';
	for (const field of fallbackFields) {
		const def = elementDefinitionMap.get(field);
		const raw = normalizedElements?.[field];
		if (DATE_TYPES.has(def?.dataType)) {
			// A list of dates stamps the title with its EARLIEST, the same one
			// the header and the sort read; the fallback (a card whose only
			// value is this one) still spells the whole list out.
			const one = Array.isArray(raw) ? earliestDateValue(def?.dataType, raw) : raw;
			if (!dateRaw && typeof one === 'string' && one.trim()) {
				dateRaw = one;
				dateDef = def;
				dateFallback = formatFallbackValue(def, raw);
			}
			continue;
		}
		if (!identity) {
			const formatted = formatFallbackValue(def, raw);
			if (formatted) identity = formatted;
		}
		if (identity && dateRaw) break;
	}
	if (identity && dateRaw) {
		// Precision-aware, and the same short label as before for everything
		// else: `formatShortDateByPrecision` IS `formatShortDate` at 'day'.
		const short = formatShortDateByPrecision(dateRaw, precisionOf(dateDef));
		return short ? `${identity} · ${short}` : identity;
	}
	return identity || dateFallback || '';
};

const normalizeElementsForLookup = (elements, elementDefinitions, resolveLookupValue) => {
	if (!resolveLookupValue || !Array.isArray(elementDefinitions) || !elementDefinitions.length) return elements;
	const map = new Map();
	elementDefinitions.forEach(def => { if (def?.name) map.set(def.name, def); });
	const normalized = { ...elements };
	Object.keys(elements || {}).forEach(key => {
		const def = map.get(key);
		if (def?.dataType === 'card-lookup') {
			normalized[key] = resolveLookupValue(elements[key]);
		}
	});
	return normalized;
};

/**
 * The RECORD tokens — the item's own identity, which no element holds.
 *
 * They exist because the single most common thing to print in a barcode is
 * the record itself, and until now a template could only name elements. The
 * underscore namespace is free: an element name cannot begin with one
 * (keepr-api utils/cardDefUtils.js name rules), so nothing an author can
 * create shadows these. The guard below makes that explicit rather than
 * assumed — an element really named `_id`, however it got there, still wins,
 * because the author's own data outranks a convenience token.
 *
 * `_url` is the app deep link, which requires a SIGNED-IN reader. A publicly
 * scannable code is a different feature — it would need a shareLinks
 * temp-link target — and is deliberately not half-built here.
 */
export const RECORD_TOKENS = ['_id', '_url', '_collection', '_card'];

export function recordTokenValues(record) {
	if (!record) return {};
	const id = record._id ? String(record._id) : '';
	const collectionId = record.collection_id ? String(record.collection_id) : '';
	const origin = siteOrigin();
	return {
		_id: id,
		_collection: collectionId,
		_card: record.card_id ? String(record.card_id) : '',
		// The item's short URL when it has a code (/i/<code>, KPE-002): shorter
		// on a label, a smaller QR code, and still right after the item moves.
		_url: id && isCode(record.code) ? `${origin}/i/${record.code}`
			: (id && collectionId ? `${origin}/collections/${collectionId}/items/${id}` : ''),
	};
}

/**
 * A template rendered against ONE item's values, with **no fallback**: where
 * `buildItemDisplayValue` must always produce something to call a record by,
 * a text block on a page prints exactly what its tokens resolve to and
 * nothing when they resolve to nothing.
 *
 * Shares the token machinery with the title path — a lookup resolved to its
 * text before a formatter sees it, formatters applied with the element's own
 * options — so `{{weight | unit(kg)}}` means the same thing on paper as it
 * does in a card's title.
 *
 * `record` is optional and adds the four RECORD_TOKENS above. `escapeValue`
 * is the seam a GS1 barcode payload needs: the AI skeleton belongs to the
 * template author, but an interpolated item value carrying a stray
 * parenthesis would rewrite the AI stream, so the caller can escape every
 * resolved VALUE without touching the literal text around it.
 */
export function renderTemplateForItem({ template, elements = {}, elementDefinitions = [], resolveLookupValue, record = null, escapeValue = null }) {
	if (!template) return '';
	const normalized = normalizeElementsForLookup(elements || {}, elementDefinitions, resolveLookupValue);
	const byName = new Map(
		(elementDefinitions || []).map(def => (def?.name ? [def.name, def] : null)).filter(Boolean)
	);
	const tokens = record ? recordTokenValues(record) : {};
	const escape = typeof escapeValue === 'function' ? escapeValue : (v => v);
	return renderDisplayTemplate(template, (segment) => {
		// An element of that name always wins: the token is a convenience,
		// the card is the contract.
		if (!byName.has(segment.name) && Object.prototype.hasOwnProperty.call(tokens, segment.name)) {
			return escape(toStringValue(applyFormatters(tokens[segment.name], 'text-small', segment.formatters) ?? ''));
		}
		const def = byName.get(segment.name);
		const raw = templateTokenInput(def, normalized?.[segment.name], segment.formatters);
		return escape(toStringValue(applyFormatters(raw, def?.dataType, segment.formatters, def?.options) ?? ''));
	});
}

export function buildItemDisplayValue({
	template,
	elements = {},
	fallbackFields = [],
	elementDefinitions = [],
	resolveLookupValue,
	name,
	id,
	emptyElementPlaceholders = false,
}) {
	const normalizedElements = normalizeElementsForLookup(elements || {}, elementDefinitions, resolveLookupValue);
	const elementDefinitionMap = new Map(
		(elementDefinitions || [])
			.map(def => def?.name ? [def.name, def] : null)
			.filter(Boolean)
	);
	const rendered = (renderDisplayTemplate(template, (segment) => {
		const def = elementDefinitionMap.get(segment.name);
		const raw = templateTokenInput(def, normalizedElements?.[segment.name], segment.formatters);
		const formatted = toStringValue(applyFormatters(raw, def?.dataType, segment.formatters, def?.options) ?? '');
		if (emptyElementPlaceholders && !formatted.trim()) {
			return `[${def?.label?.singular || segment.name}]`;
		}
		return formatted;
	}) || '').trim();
	if (rendered) return rendered;

	if (Array.isArray(fallbackFields) && fallbackFields.length) {
		const composed = composeDefaultDisplayValue(fallbackFields, elementDefinitionMap, normalizedElements);
		if (composed) return composed;
	}

	const generic = firstNonEmptyString(
		Object.entries(normalizedElements || {})
			.map(([key, val]) => formatFallbackValue(elementDefinitionMap.get(key), val))
	);
	if (generic) return generic;

	// No tag fallback: tags are retired (docs/plans/tags.md §4.1), and the
	// API's display title dropped the same step (keepr-api
	// utils/displayTitle.js), so the two titles still agree.
	if (name && name.trim().length) return name.trim();
	if (id) return id;
	return '';
}
